// src/features/roles/chargeur.ts — SCE Issues
// Les rôles du panneau d'administration (22/10) au démarrage : copie locale
// posée AVANT le premier rendu (main.tsx), puis SharePoint, qui fait foi.
//
// Même schéma que les couleurs métier (features/couleurs-metier) : sans la
// copie, le premier écran montrerait les droits du code puis changerait. Ici
// la copie compte davantage encore : elle reconnaît un iPad partagé ajouté
// depuis le panneau dès l'ouverture de l'appli.
//
// Un changement reçu de SharePoint passe par le registre (config/registreRoles),
// qui prévient App.tsx : l'appli se remonte avec la nouvelle liste.

import { useEffect, useSyncExternalStore } from 'react'
import { lireRolesEnregistres } from '@/api/roles.api'
import { abonnerRoles, definirRolesCharges, versionRoles } from '@/config/registreRoles'
import { rolesParDefaut } from '@/config/rolesDefaut'
import { lireRoles, type Roles } from '@/utils/roles'

const CLE_CACHE = 'sce-issues.roles'

/** Pose des rôles dans le registre et les garde pour le prochain démarrage (null : valeurs du code). */
export function appliquerRoles(r: Roles | null, options?: { prevenir?: boolean }): void {
  definirRolesCharges(r, options)
  try {
    if (r) localStorage.setItem(CLE_CACHE, JSON.stringify(r))
    else localStorage.removeItem(CLE_CACHE)
  } catch {
    // Stockage indisponible : SharePoint corrigera après le premier rendu.
  }
}

/** main.tsx, avant le premier rendu : les rôles connus au dernier passage. */
export function appliquerRolesMemorises(): void {
  try {
    const brut = localStorage.getItem(CLE_CACHE)
    if (brut) definirRolesCharges(lireRoles(JSON.parse(brut), rolesParDefaut()))
  } catch {
    // Copie illisible : valeurs du code, SharePoint corrigera.
  }
}

/** Monté une fois dans App.tsx, sous l'authentification. Un échec est silencieux. */
export function ChargeurRoles(): null {
  useEffect(() => {
    let annule = false
    lireRolesEnregistres()
      .then((r) => {
        if (!annule) appliquerRoles(r)
      })
      .catch((err: unknown) => {
        // eslint-disable-next-line no-console
        console.warn('[roles] lecture impossible, rôles conservés', err)
      })
    return () => {
      annule = true
    }
  }, [])
  return null
}

/** Change à chaque nouvelle liste de rôles : App.tsx s'en sert de clé pour remonter l'appli. */
export function useVersionRoles(): number {
  return useSyncExternalStore(abonnerRoles, versionRoles)
}
